"use client"

import { AUTH_TOKEN } from "constants/AuthConstant"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { setEmployeesState, setEmployeeState } from "store/slices/employees"
import { isAuth } from "./isAuth"
import { setAuthUser, signOutSuccess } from "store/slices/authSlice"
import { refreshAccessToken, isRefreshingToken } from "refreshToken"

const useEmployees = () => {
  const [employees, setEmployees] = useState(null)
  const [employee, setEmployee] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  
  const base_url = process.env.REACT_APP_API_URL;

  const POINTAGE_BASE_URL = `${base_url}/pointage`;
  const url_getAllEmployees = base_url + "/employees";
  const url_filterByPresence = base_url + "/employees/filter/by-presence";
  const url_getAllEducator = `${base_url}/employees/educators`;
  const url_addEmployee = base_url + "/employees";
  const url_search = base_url + "/employees/search?q=";
  const url_updateEmployee = base_url + "/employees";
  
  const dispatch = useDispatch()
  const tokenFromState = useSelector((state) => state.auth.token)
  
  // Vérifie la cohérence du token entre le state et le localStorage
  if (localStorage.getItem(AUTH_TOKEN) !== tokenFromState) {
    localStorage.removeItem(AUTH_TOKEN)
    dispatch(signOutSuccess())
    dispatch(setAuthUser({}))
  }

  // Fonction générique pour les appels API avec gestion du token
  const authFetch = async (url, options = {}, navigate) => {
    let token = tokenFromState || localStorage.getItem(AUTH_TOKEN)
    
    // Si pas de token et pas de refresh en cours, on tente un refresh
    if (!token && !isRefreshingToken()) {
      token = await refreshAccessToken()
    }

    if (!token) {
      throw new Error("Authentication required")
    }

    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    })

    // Si token expiré, on tente un refresh
    if (response.status === 401) {
      token = await refreshAccessToken()
      if (!token) {
        localStorage.removeItem(AUTH_TOKEN)
        dispatch(signOutSuccess())
        dispatch(setAuthUser({}))
        if (navigate) navigate("/login")
        throw new Error("Session expired")
      }

      // On réessaye avec le nouveau token
      return fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      })
    }

    return response
  }

  // Gestion centralisée des erreurs API
  const handleApiError = (errorData) => {
    if (!isAuth(errorData)) {
      localStorage.removeItem(AUTH_TOKEN)
      dispatch(signOutSuccess())
      dispatch(setAuthUser({}))
    }

    if (errorData?.statusCode === 500) {
      setError("Erreur interne du serveur")
      return
    }

    if (Array.isArray(errorData?.data?.message)) {
      setError(errorData.data.message[0])
      return
    }

    if (errorData?.data?.message) {
      setError(errorData.data.message)
      return
    }

    if (errorData?.message) {
      setError(errorData.message)
      return
    }

    setError(`Erreur interne du serveur`)
  }

  const createPointage = async (employeeId, presence, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(POINTAGE_BASE_URL, {
        method: "POST",
        body: JSON.stringify({ employeeId, presence }),
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      return await response.json()
    } catch (err) {
      console.error("Failed to create pointage", err)
      setError(err.message || "Failed to create pointage")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const updatePointage = async (pointageId, presence, date, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${POINTAGE_BASE_URL}/${pointageId}`, {
        method: "PUT",
        body: JSON.stringify({ presence, date }),
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      return await response.json()
    } catch (err) {
      console.error("Failed to update pointage", err)
      setError(err.message || "Failed to update pointage")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const getPointageById = async (pointageId, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${POINTAGE_BASE_URL}/${pointageId}`, {
        method: "GET",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return null
      }

      return await response.json()
    } catch (err) {
      console.error("Failed to get pointage", err)
      setError(err.message || "Failed to get pointage")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const filterEmployeesByPresence = async (status, date, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      let url = `${url_filterByPresence}?status=${status}&limit=100&page=1`
      if (date) {
        url += `&date=${date}`
      }

      const response = await authFetch(url, {
        method: "GET",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        console.error("Erreur de filtrage:", errorData)
        handleApiError(errorData)
        return null
      }

      const responseData = await response.json()
      const employeesData = responseData.data || []
      setEmployees(employeesData)
      dispatch(setEmployeesState(employeesData))

      return employeesData
    } catch (err) {
      console.error("Failed to filter employees by presence", err)
      setError(err.message || "Failed to filter employees by presence")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const getAllEmployees = async (navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(url_getAllEmployees, {
        method: "GET",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setEmployees(data)
      dispatch(setEmployeesState(data))
    } catch (err) {
      console.error("Failed to get employees", err)
      setError(err.message || "Failed to get employees")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const getAllEducators = async (navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(url_getAllEducator, {
        method: "GET",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setEmployees(data)
      dispatch(setEmployeesState(data))
    } catch (err) {
      console.error("Failed to get educators", err)
      setError(err.message || "Failed to get educators")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const addEmployee = async (data, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(url_addEmployee, {
        method: "POST",
        body: JSON.stringify(data),
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }
      
      const responseData = await response.json()
      setEmployee(responseData)
      await getAllEmployees(navigate)
    } catch (err) {
      console.error("Failed to add employee", err)
      setError(err.message || "Failed to add employee")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const deleteEmployee = async (id, navigate) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_addEmployee}/${id}`, {
        method: "DELETE",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }
      
      setEmployee([])
      await getAllEmployees(navigate)
    } catch (err) {
      console.error("Failed to delete employee", err)
      setError(err.message || "Failed to delete employee")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const getEmployeesByLastNameAndFirstName = async (q, navigate) => {
    if (!q) return
    
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_search}${q}`, {
        method: "GET",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      setEmployees(data)
      dispatch(setEmployeesState(data))
    } catch (err) {
      console.error("Failed to get employees", err)
      setError(err.message || "Failed to get employees")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const updateEmployee = async (data, id, navigate) => {
    setEmployee(null)
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_updateEmployee}/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }
      
      const responseData = await response.json()
      setEmployee(responseData)
      await getAllEmployees(navigate)
    } catch (err) {
      console.error("Failed to update employee", err)
      setError(err.message || "Failed to update employee")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  const getEmployeeById = async (id, navigate) => {
    if (!id) return
    
    setEmployee(null)
    setIsLoading(true)
    setError(null)

    try {
      const response = await authFetch(`${url_getAllEmployees}/${id}`, {
        method: "GET",
      }, navigate)

      if (!response.ok) {
        const errorData = await response.json()
        handleApiError(errorData)
        return
      }

      const data = await response.json()
      dispatch(setEmployeeState(data))
      return data
    } catch (err) {
      console.error("Failed to get employee", err)
      setError(err.message || "Failed to get employee")
      throw err
    } finally {
      setIsLoading(false)
    }
  }

  return {
    getEmployeeById,
    updateEmployee,
    getEmployeesByLastNameAndFirstName,
    deleteEmployee,
    addEmployee,
    getAllEducators,
    getAllEmployees,
    employee,
    setEmployee,
    employees,
    setEmployees,
    isLoading,
    error,
    setError,
    createPointage,
    updatePointage,
    getPointageById,
    filterEmployeesByPresence,
  }
}

export default useEmployees